import {Injectable} from "@angular/core";
import {FormGroup} from "@angular/forms";

/** Named form groups to snapshot together under a single draft key. */
export type DraftGroups = Record<string, FormGroup | undefined>;

/**
 * Keeps an in-progress form recoverable across a page reload.
 *
 * iOS Safari discards backgrounded tabs under memory pressure and reloads them
 * when the user returns — which is exactly what happens when someone leaves a
 * booking half-filled to look an address up in Contacts or Maps. Angular
 * restarts with empty controls and everything typed so far is gone.
 *
 * Drafts live in sessionStorage: scoped to the one tab, cleared when it closes,
 * and preserved across the discard-and-restore cycle we are guarding against.
 *
 * Storage is a convenience, never a dependency. Safari Private Browsing can
 * refuse writes outright, so every access is guarded and a failure degrades to
 * "no draft" rather than breaking the form this is meant to protect.
 */
@Injectable({providedIn: "root"})
export class FormDraftService {
    /**
     * Snapshots the enabled values of each named group.
     *
     * `FormGroup.value` omits disabled controls, and in this app those are the
     * server-owned ones (main phone, special terms, shipper comment, the
     * user-friendly city). Excluding them is therefore automatic: a restored
     * draft can never shadow freshly loaded server data.
     */
    save(key: string, groups: DraftGroups): void {
        const snapshot: Record<string, unknown> = {};

        for (const [name, group] of Object.entries(groups)) {
            if (group) snapshot[name] = group.value;
        }

        try {
            sessionStorage.setItem(key, JSON.stringify(snapshot));
        } catch (error) {
            console.warn("Could not save form draft", error);
        }
    }

    /**
     * Patches a saved snapshot back over the matching groups.
     *
     * @returns true if anything was restored.
     */
    restore(key: string, groups: DraftGroups): boolean {
        const snapshot = this._read(key);
        if (!snapshot) return false;

        let restored = false;

        for (const [name, group] of Object.entries(groups)) {
            const values = snapshot[name];
            if (!group || !values || typeof values !== "object") continue;

            const draft = values as Record<string, unknown>;

            // emitEvent: false because some controls drive cascades — restoring
            // `iAmPickupContact: true` would re-run its valueChanges handler and
            // overwrite the contact fields being restored alongside it, losing
            // any edit the customer made after ticking the box.
            group.patchValue(draft, {emitEvent: false});

            // The customer had typed in these fields before the tab died, so
            // reproduce that state: pristine controls would hide validation
            // errors on restored values until the field was touched again.
            for (const [controlName, value] of Object.entries(draft)) {
                if (value === null || value === "") continue;
                group.get(controlName)?.markAsDirty();
            }

            restored = true;
        }

        return restored;
    }

    clear(key: string): void {
        try {
            sessionStorage.removeItem(key);
        } catch (error) {
            console.warn("Could not clear form draft", error);
        }
    }

    private _read(key: string): Record<string, unknown> | null {
        let raw: string | null;

        try {
            raw = sessionStorage.getItem(key);
        } catch (error) {
            console.warn("Could not read form draft", error);
            return null;
        }

        if (!raw) return null;

        try {
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : null;
        } catch {
            // Nothing recoverable in a corrupt entry — drop it so it cannot keep
            // failing on every subsequent load.
            this.clear(key);
            return null;
        }
    }
}
